import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import PendingApprovals from './components/PendingApprovals';
import QuickActions from './components/QuickActions';
import SystemStatus from './components/SystemStatus';
import { dashboardService } from '../../services/dashboardService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    totalDocuments: 0,
    activeUsers: 0,
    pendingChanges: 0,
    storageUsed: { formatted: '0 GB', gb: 0 },
    recentActivities: [],
    pendingApprovals: []
  });

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const metrics = await dashboardService?.getDashboardMetrics();
        setDashboardData(metrics);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Generate metrics data based on real database values
  const metricsData = [
    {
      title: "Total Documents",
      value: isLoading ? "..." : dashboardData?.totalDocuments?.toString() || "0",
      change: isLoading ? "Loading..." : dashboardData?.totalDocuments > 0 
        ? `${dashboardData?.totalDocuments} documents in system`
        : "No documents yet",
      changeType: dashboardData?.totalDocuments > 0 ? "positive" : "neutral",
      icon: "FileText",
      iconColor: "bg-primary"
    },
    {
      title: "Pending Changes",
      value: isLoading ? "..." : dashboardData?.pendingChanges?.toString() || "0",
      change: isLoading ? "Loading..." : dashboardData?.pendingChanges > 0 
        ? `${dashboardData?.pendingChanges} awaiting approval`
        : "No pending changes",
      changeType: dashboardData?.pendingChanges > 0 ? "warning" : "positive",
      icon: "GitBranch",
      iconColor: "bg-warning"
    },
    {
      title: "Active Users",
      value: isLoading ? "..." : dashboardData?.activeUsers?.toString() || "0",
      change: isLoading ? "Loading..." : dashboardData?.activeUsers > 0 
        ? `${dashboardData?.activeUsers} users registered`
        : "No active users",
      changeType: dashboardData?.activeUsers > 0 ? "positive" : "neutral",
      icon: "Users",
      iconColor: "bg-success"
    },
    {
      title: "Storage Used",
      value: isLoading ? "..." : dashboardData?.storageUsed?.formatted || "0 GB",
      change: isLoading ? "Loading..." : dashboardData?.storageUsed?.gb > 0 
        ? `${((dashboardData?.storageUsed?.gb / 1000) * 100)?.toFixed(1)}% of 1TB capacity`
        : "0% of total capacity",
      changeType: "neutral",
      icon: "HardDrive",
      iconColor: "bg-accent"
    }
  ];

  // System status - all operational but show real usage
  const systemStatusData = {
    services: [
      {
        name: "Document Storage",
        description: `${dashboardData?.totalDocuments} documents stored`,
        status: "operational"
      },
      {
        name: "AI Analysis Engine",
        description: "Document analysis and insights", 
        status: "operational"
      },
      {
        name: "Search Service",
        description: "Document search and indexing",
        status: "operational"
      },
      {
        name: "User Authentication",
        description: `${dashboardData?.activeUsers} active users`,
        status: "operational"
      }
    ],
    lastUpdated: new Date()
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMetricClick = (metric) => {
    switch (metric?.title) {
      case "Total Documents":
        navigate('/document-base-management');
        break;
      case "Pending Changes":
        navigate('/change-management');
        break;
      case "Active Users":
        navigate('/user-management');
        break;
      default:
        break;
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      // In a real app, this would make an API call to approve the request
      console.log('Approving request:', approvalId);
      // Refresh data after approval
      const metrics = await dashboardService?.getDashboardMetrics();
      setDashboardData(metrics);
    } catch (err) {
      console.error('Failed to approve request:', err);
    }
  };

  const handleReject = async (approvalId) => {
    try {
      // In a real app, this would make an API call to reject the request
      console.log('Rejecting request:', approvalId);
      // Refresh data after rejection
      const metrics = await dashboardService?.getDashboardMetrics();
      setDashboardData(metrics);
    } catch (err) {
      console.error('Failed to reject request:', err);
    }
  };

  const handleUploadDocument = () => {
    navigate('/document-base-management');
  };

  const handleCreateDocumentBase = () => {
    navigate('/document-base-management');
  };

  const handleAdvancedSearch = () => {
    navigate('/search-results');
  };

  const handleViewAllActivities = () => {
    navigate('/document-base-management');
  };

  const handleViewAllApprovals = () => {
    navigate('/change-management');
  };

  // Calculate today's, this week's, and this month's uploads
  const getUploadStats = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.setDate(now.getDate() - 7));
    const monthAgo = new Date(now.setMonth(now.getMonth() - 1));

    const todayCount = dashboardData?.recentActivities?.filter(activity => 
      activity?.type === 'document_upload' && 
      new Date(activity.timestamp) >= today
    )?.length || 0;

    const weekCount = dashboardData?.recentActivities?.filter(activity => 
      activity?.type === 'document_upload' && 
      new Date(activity.timestamp) >= weekAgo
    )?.length || 0;

    const monthCount = dashboardData?.recentActivities?.filter(activity => 
      activity?.type === 'document_upload' && 
      new Date(activity.timestamp) >= monthAgo
    )?.length || 0;

    return { todayCount, weekCount, monthCount };
  };

  const uploadStats = getUploadStats();

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleSidebarToggle} isMenuOpen={isSidebarOpen} />
        <Sidebar 
          isCollapsed={isSidebarCollapsed} 
          isOpen={isSidebarOpen} 
          onClose={() => setIsSidebarOpen(false)} 
        />
        <main className={`pt-16 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Dashboard</h3>
              <p className="text-destructive/80">{error}</p>
              <button 
                onClick={() => window.location?.reload()} 
                className="mt-3 px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleSidebarToggle} isMenuOpen={isSidebarOpen} />
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                {isLoading 
                  ? "Loading system overview..." 
                  : dashboardData?.totalDocuments > 0 
                    ? `System overview: ${dashboardData?.totalDocuments} documents, ${dashboardData?.activeUsers} users active`
                    : "Welcome! Get started by uploading documents and creating your first document base."
                }
              </p>
            </div>
            <div className="text-sm text-muted-foreground">
              Last updated: {new Date()?.toLocaleString()}
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                iconColor={metric?.iconColor}
                onClick={() => handleMetricClick(metric)}
              />
            ))}
          </div>

          {/* Quick Actions */}
          <QuickActions
            onUploadDocument={handleUploadDocument}
            onCreateDocumentBase={handleCreateDocumentBase}
            onAdvancedSearch={handleAdvancedSearch}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <ActivityFeed
              activities={dashboardData?.recentActivities}
              onViewAll={handleViewAllActivities}
              isLoading={isLoading}
            />

            {/* Pending Approvals */}
            <PendingApprovals
              approvals={dashboardData?.pendingApprovals}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewAll={handleViewAllApprovals}
              isLoading={isLoading}
            />
          </div>

          {/* System Status */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <SystemStatus status={systemStatusData} />
            </div>
            
            {/* Additional Info Cards */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Storage Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Documents</span>
                    <span className="text-sm font-medium text-foreground">
                      {isLoading ? "Loading..." : dashboardData?.storageUsed?.formatted}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${Math.min((dashboardData?.storageUsed?.gb / 1000) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>
                      {isLoading 
                        ? "Loading..." 
                        : `${((dashboardData?.storageUsed?.gb / 1000) * 100)?.toFixed(1)}% used`
                      }
                    </span>
                    <span>1 TB total</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Recent Uploads</h3>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium text-foreground">Today:</span>
                    <span className="text-muted-foreground ml-2">
                      {isLoading ? "Loading..." : `${uploadStats?.todayCount} documents`}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-foreground">This week:</span>
                    <span className="text-muted-foreground ml-2">
                      {isLoading ? "Loading..." : `${uploadStats?.weekCount} documents`}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-foreground">This month:</span>
                    <span className="text-muted-foreground ml-2">
                      {isLoading ? "Loading..." : `${uploadStats?.monthCount} documents`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;